import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

/**
 * Gate global de MFA (TOTP) obrigatório para todos os usuários autenticados.
 *
 * Regras:
 *  - Usuário NÃO autenticado: passa direto (a loja pública continua acessível
 *    sem fricção; o gate só atua depois do login).
 *  - Autenticado SEM fator TOTP verificado → forçado para /mfa/setup.
 *  - Autenticado COM fator mas sessão ainda em aal1 → forçado para /mfa/challenge.
 *  - Autenticado com aal2 → tudo liberado.
 *
 * Rotas isentas (sempre acessíveis para evitar deadlocks):
 *  - /mfa/setup, /mfa/challenge → as próprias telas de MFA
 *  - /login, /admin/login, /admin/forgot-password, /admin/reset-password →
 *    para permitir logout / troca de conta sem ficar preso
 */
const MFA_ROUTES = ["/mfa/setup", "/mfa/challenge"];
const AUTH_ROUTES = [
  "/login",
  "/admin/login",
  "/admin/forgot-password",
  "/admin/reset-password",
];

const MfaGate = ({ children }: { children: ReactNode }) => {
  const { user, sessionReady, mfaReady, needsMfaSetup, needsMfaChallenge } = useAuth();
  const location = useLocation();
  const path = location.pathname;

  // Antes da sessão restaurar, não decidimos nada — render normal.
  if (!sessionReady) return <>{children}</>;

  // Sem sessão, sem gate.
  if (!user) return <>{children}</>;

  // Estamos numa rota de MFA ou auth — sempre permite renderizar.
  if (MFA_ROUTES.includes(path) || AUTH_ROUTES.includes(path)) {
    return <>{children}</>;
  }

  // Aguardando o check de MFA: mostra um spinner em vez de soltar a UI sensível.
  if (!mfaReady) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (needsMfaSetup) {
    return <Navigate to="/mfa/setup" replace state={{ from: path }} />;
  }
  if (needsMfaChallenge) {
    return <Navigate to="/mfa/challenge" replace state={{ from: path }} />;
  }

  return <>{children}</>;
};

export default MfaGate;